import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AccommodationSectionHeading } from '@vakantiesnl/components/src/atoms/AccommodationSectionHeading';
import { Alert } from '@vakantiesnl/components/src/atoms/Alert';
import { TripOffers as TripOffersComponent } from '@vakantiesnl/components/src/organisms/TripOffers';
import { getOfferOffset } from '@vakantiesnl/components/src/utils/accommodation/offerUtils';
import { useBreakpoints } from '@vakantiesnl/components/src/utils/useBreakpoints';
import { useMicroCopyContext } from '@vakantiesnl/services/src/context/microCopyContext';
import { useAccommodationContext } from '@vakantiesnl/services/src/context/useAccommodationContext';
import { useTripFilterContext } from '@vakantiesnl/services/src/context/useTripFilterContext';
import { useGetAccommodationByEntityId } from '@vakantiesnl/services/src/hooks/queries';
import {
	type DepartureDateOffersResponse,
	type GetDepartureDateOffersQuery,
	useGetDepartureDatePackageOffers,
	useGetOfferFilters,
} from '@vakantiesnl/services/src/hooks/queries/packageOffers';
import { useSearchUrlBuilder } from '@vakantiesnl/services/src/hooks/useSearchUrlBuilder';
import { useAccoOfferStore } from '@vakantiesnl/services/src/stores/accommodationStore';
import { formatPaxQuery } from '@vakantiesnl/services/src/util/partyUtils';
import { type Search } from '@vakantiesnl/types/src';
import dynamic from 'next/dynamic';

import { useStyles } from './trip-offers.styles';
import { TripFilterMenuBlock } from '../trip-filter-menu-block';

const CompassLoader = dynamic(
	() =>
		import('@vakantiesnl/components/src/atoms/CompassLoader').then((mod) => ({
			default: mod.CompassLoader,
		})),
	{ ssr: false },
);

const CAROUSEL_STEP_SIZE = 3;

export const PackageTripOffersInfiniteScroll: FC = () => {
	const [buttonLabel, setButtonLabel] = useState<string | null>(null);
	const [initialOfferSelection, setInitialOfferSelection] = useState(true);
	const { classes } = useStyles();
	const { accommodation } = useGetAccommodationByEntityId();
	const { isDesktop } = useBreakpoints();
	const carouselRange = useMemo(() => (isDesktop ? 7 : 5), [isDesktop]);

	const microCopies = useMicroCopyContext();
	const {
		state: { tripsOfferData },
		dispatch: { setDepartureDateOffer },
	} = useAccommodationContext();
	const {
		state: filtersState,
		computed: { durations },
		dispatch: {
			updateSelectedDirectOnly,
			updateSelectedFlight,
			updateSelectedRoomType,
			updateSelectedMealPlan,
			updateSelectedTrip,
		},
	} = useTripFilterContext();
	const offerFilters = useGetOfferFilters();

	const country = accommodation.parents[0];
	const region = accommodation.parents[1];

	const noOffersLink = useSearchUrlBuilder({
		departure_date: offerFilters.departureDate ? [offerFilters.departureDate] : [],
		countries: country && country.slug ? [{ entityId: country.heliosId.toString(), slug: country.slug }] : [],
		regions: region && region.slug ? [{ entityId: region.heliosId.toString(), slug: region.slug }] : [],
		pax: formatPaxQuery(offerFilters.party),
		durations: durations, // use original durations due to different offset that is expected in the lister url
		airports: offerFilters.airports,
		mealplans: offerFilters.mealplans,
	});

	const departureDateOffer = useAccoOfferStore((s) => s.departureDateOffer);
	const isLoadingDepartureDateOffers = useAccoOfferStore((s) => s.isDepartureDateLoading);
	const {
		departureDateOffers,
		fetchNextDepartureDateOffers,
		fetchPrevDepartureDateOffers,
		hasNextDepartureDateOffers,
		hasPrevDepartureDateOffers,
		isFetchingDepartureDateOffers,
	} = useGetDepartureDatePackageOffers();

	// Offset to start from in the price bars
	const [rangeStart, setSelectedRange] = useState<number>(-1);

	const stringFilters = JSON.stringify(offerFilters);
	const prevFilters = useRef(stringFilters);
	const initialLoad = useRef(true);

	// Update selected offer and offset only initially OR when filters change
	useEffect(() => {
		// When there are no offers, reset the selected offer and refs (f.e. after filtering)
		if (!isLoadingDepartureDateOffers && !departureDateOffers?.all.length) {
			prevFilters.current = stringFilters;
			initialLoad.current = true;
			setDepartureDateOffer(undefined);
			setSelectedRange(0);
			return;
		}

		// When loading or if the filters are the same, then do not set the offset, except for on the initial load
		if ((prevFilters.current === stringFilters && initialLoad.current === false) || isLoadingDepartureDateOffers) {
			return;
		}

		// Only update after initial checks on both refs are done
		prevFilters.current = stringFilters;
		initialLoad.current = false;

		const initialSelectedOffer = getInitialSelectedOffer(
			departureDateOffers,
			filtersState.selected.date || offerFilters.departureDate,
			offerFilters.hasUserSelectedDate,
		);

		const offset = getOfferOffset(initialSelectedOffer, departureDateOffers?.all, carouselRange);

		setDepartureDateOffer(initialSelectedOffer);
		setSelectedRange(offset);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoadingDepartureDateOffers, departureDateOffers?.all]);

	const onDepartureDateOfferSelect = useCallback(
		(offerId: string) => {
			if (!isLoadingDepartureDateOffers) {
				const selectedOffer = departureDateOffers?.all.find((o) => o.id === offerId);

				updateSelectedTrip(selectedOffer?.departureDateYYYYMMDD);

				if (selectedOffer) {
					if (initialOfferSelection) setInitialOfferSelection(false);
					setDepartureDateOffer(selectedOffer);
				}

				updateSelectedDirectOnly(undefined);
				updateSelectedFlight(undefined);
				updateSelectedRoomType(undefined);
				updateSelectedMealPlan(undefined);
			}
		},
		[
			departureDateOffers?.all,
			initialOfferSelection,
			isLoadingDepartureDateOffers,
			setDepartureDateOffer,
			updateSelectedDirectOnly,
			updateSelectedFlight,
			updateSelectedMealPlan,
			updateSelectedRoomType,
			updateSelectedTrip,
		],
	);

	const onNoOffersLinkClick = useCallback(
		() =>
			import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
				import('@vakantiesnl/services/src/gtm/accommodationPage').then(({ noOffersLinkClick }) =>
					track(
						noOffersLinkClick(
							noOffersLink,
							offerFilters.party,
							offerFilters.departureDate ? [offerFilters.departureDate] : null,
							offerFilters.mealplans,
							durations,
							offerFilters.airports,
							accommodation.heliosId,
						),
					),
				),
			),
		[
			accommodation.heliosId,
			noOffersLink,
			offerFilters.airports,
			offerFilters.departureDate,
			durations,
			offerFilters.mealplans,
			offerFilters.party,
		],
	);

	useEffect(() => {
		if (accommodation.isBookable && departureDateOffers && !isLoadingDepartureDateOffers) {
			import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
				import('@vakantiesnl/services/src/gtm/accommodationPage').then(({ tripOffersLoad }) => {
					track(tripOffersLoad(accommodation, departureDateOffers.all, 'load', 'view', null));
				}),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accommodation, isLoadingDepartureDateOffers]);

	useEffect(() => {
		if (buttonLabel && accommodation.isBookable && departureDateOffers && !isLoadingDepartureDateOffers) {
			import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
				import('@vakantiesnl/services/src/gtm/accommodationPage').then(({ tripOffersLoad }) => {
					track(tripOffersLoad(accommodation, departureDateOffers.all, 'action', 'click', buttonLabel));
					setButtonLabel(null);
				}),
			);
		}
	}, [accommodation, isLoadingDepartureDateOffers, departureDateOffers, buttonLabel]);

	useEffect(() => {
		if (!departureDateOffer || tripsOfferData?.loading || !departureDateOffers) {
			return;
		}

		import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
			import('@vakantiesnl/services/src/gtm/accommodationPage').then(({ pricebarSelect }) =>
				track(
					pricebarSelect(departureDateOffer, accommodation, departureDateOffers.party, initialOfferSelection),
				),
			),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [departureDateOffer?.id]);

	const [isFetchingPreviousOffers, setIsFetchingPreviousOffers] = useState(false);
	const [isFetchingNextOffers, setIsFetchingNextOffers] = useState(false);
	const isFetchingOffers = isFetchingDepartureDateOffers || isFetchingNextOffers || isFetchingPreviousOffers;

	// Automatically refetch next 30 days when the end of the list is reached
	useEffect(() => {
		if (isFetchingOffers) {
			return;
		}

		if (!hasNextDepartureDateOffers) {
			setIsFetchingNextOffers(false);
			return;
		}

		const rangeEnd = rangeStart + carouselRange;
		const hasReachedEnd = rangeEnd >= (departureDateOffers?.all || []).length;

		if (hasReachedEnd) {
			setIsFetchingNextOffers(true);
			fetchOffers(fetchNextDepartureDateOffers, 'next').then(() => {
				setIsFetchingNextOffers(false);
			});
		}
	}, [
		isFetchingOffers,
		fetchNextDepartureDateOffers,
		rangeStart,
		departureDateOffers?.all,
		carouselRange,
		hasNextDepartureDateOffers,
		setIsFetchingNextOffers,
	]);

	// Automatically refetch previous 30 days when the start of the list is reached,
	// then update the range offset due to the list being prepended
	useEffect(() => {
		if (!departureDateOffers?.all.length || isFetchingOffers) {
			return;
		}

		if (!hasPrevDepartureDateOffers) {
			setIsFetchingPreviousOffers(false);
			return;
		}

		const hasReachedStart = rangeStart === 0;

		if (hasReachedStart) {
			setIsFetchingPreviousOffers(true);

			fetchOffers(fetchPrevDepartureDateOffers, 'prev').then((prependedItemCount) => {
				setSelectedRange((prevRange) => prevRange + prependedItemCount);
				setIsFetchingPreviousOffers(false);
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isFetchingOffers, fetchPrevDepartureDateOffers, rangeStart, carouselRange, hasPrevDepartureDateOffers]);

	const onClickDepartEarlier = useCallback(
		(buttonLabel: string) => {
			if (!departureDateOffers?.all.length || isFetchingDepartureDateOffers) return;

			setButtonLabel(buttonLabel);
			setSelectedRange((prevRange) => Math.max(prevRange - CAROUSEL_STEP_SIZE, 0));
		},
		[departureDateOffers?.all, isFetchingDepartureDateOffers],
	);

	const onClickDepartLater = useCallback(
		(buttonLabel: string) => {
			if (!departureDateOffers?.all.length || isFetchingDepartureDateOffers) return;

			setButtonLabel(buttonLabel);
			setSelectedRange((prevRange) => Math.min(prevRange + CAROUSEL_STEP_SIZE, departureDateOffers.all.length));
		},
		[departureDateOffers?.all, isFetchingDepartureDateOffers],
	);

	const visibleDepartureDateOffers = useMemo(() => {
		if (!departureDateOffers?.all.length) return [];

		return departureDateOffers.all.slice(rangeStart, rangeStart + carouselRange);
	}, [departureDateOffers?.all, rangeStart, carouselRange]);

	return (
		<div data-cy="trip-offers-selector">
			<AccommodationSectionHeading>
				{microCopies['accommodation.departure_dates.title']}
			</AccommodationSectionHeading>
			<TripFilterMenuBlock microCopies={microCopies} />

			{!isLoadingDepartureDateOffers && !departureDateOffers?.all && (
				<Alert className={classes.alert} variant="warning" text={microCopies['tripsOffers.noresults']} />
			)}
			{isLoadingDepartureDateOffers && (
				<div className={classes.compassLoader}>
					<CompassLoader />
				</div>
			)}
			{!isLoadingDepartureDateOffers && departureDateOffers && (
				<TripOffersComponent
					microCopies={microCopies}
					selected={departureDateOffer?.id}
					onSelect={onDepartureDateOfferSelect}
					offers={visibleDepartureDateOffers}
					onClickDepartEarlier={onClickDepartEarlier}
					onClickDepartLater={onClickDepartLater}
					earlierIsSelectable={isFetchingPreviousOffers || rangeStart > 0 || hasPrevDepartureDateOffers}
					laterIsSelectable={
						isFetchingNextOffers ||
						rangeStart + carouselRange < departureDateOffers.all.length ||
						hasNextDepartureDateOffers
					}
					min={departureDateOffers.min}
					max={departureDateOffers.max}
					cheapest={departureDateOffers.cheapest}
					noOffersLink={noOffersLink}
					onNoOffersLinkClick={onNoOffersLinkClick}
					invalidPreselection={filtersState.invalidPreselection}
					carouselRange={carouselRange}
					isLoadingOffers={isFetchingOffers}
				/>
			)}
		</div>
	);
};

type FetchOffersFn =
	| GetDepartureDateOffersQuery['fetchNextDepartureDateOffers']
	| GetDepartureDateOffersQuery['fetchPrevDepartureDateOffers'];

async function fetchOffers(fetchOffersFn: FetchOffersFn, type: 'next' | 'prev'): Promise<number> {
	return fetchOffersFn().then((r) => {
		if (r.status === 'success') {
			const pageNumber = type === 'prev' ? 0 : r.data.pages.length - 1;
			const prependedItemCount = r.data.pages[pageNumber]?.all.length || 0;

			const hasMorePages = type === 'prev' ? r.hasPreviousPage : r.hasNextPage;

			if (prependedItemCount === 0 && hasMorePages) {
				return fetchOffers(fetchOffersFn, type);
			}

			return prependedItemCount;
		}

		return 0;
	});
}

function getInitialSelectedOffer(
	departureDateOffers: DepartureDateOffersResponse | undefined,
	dateToFindSelectedOffer: string | undefined,
	hasUserSelectedDate: boolean,
): Search.OfferItem | undefined {
	if (!departureDateOffers?.all.length) {
		return undefined;
	}

	const offerByDate = departureDateOffers.all.find((offer) => {
		return offer.meta?.departureDate === dateToFindSelectedOffer;
	});

	const cheapestOffer = departureDateOffers.all.find((o) => o.id === departureDateOffers.cheapest);

	// When no offer is found and no departure date was selected, return the cheapest offer
	if (!hasUserSelectedDate) {
		return cheapestOffer || offerByDate;
	}

	return offerByDate;
}
